import {resolveStaggerOptions} from '../modules/motion/staggerReveal.js';

/** @type {Promise<typeof import('../modules/focalCarousel.js')>|null} */
let modulePromise = null;

/** @type {WeakMap<HTMLElement, object>} */
const initializations = new WeakMap();
const observed = new WeakSet();
/** @type {IntersectionObserver|null} */
let observer = null;

/**
 * Behavior to initialize the FocalCarousel module on DOM elements.
 *
 * A focal carousel is a finite carousel where card widths derive from their
 * distance to the focal (centered) card: in-focus cards render at full width and
 * the cards around them squash into slivers. See the module for the full
 * layout model and the CSS custom properties that configure the sizing.
 *
 * Data attributes:
 * - data-focal-carousel: Marks the root element
 * - data-initial-index: Index of the first card of the resting focal group on first paint (default: 0)
 * - data-autoplay: 'true' drifts through the cards forever as a seamless ring (default: 'false')
 * - data-speed: Autoplay drift speed as a factor of this carousel's base speed (default: 1)
 * - data-scale-up: 'true' scales the cards up until the arrangement fills the container (default: 'false')
 * - data-interactive: 'false' takes no pointer, wheel, key, or arrow input (default: 'true')
 * - data-reveal-animation: Entrance for the rendered cards once the carousel is on screen, before any drift. An animation type of resources/js/modules/motion/staggerReveal.js; absent means no entrance
 * - data-reveal-easing, data-reveal-duration, data-reveal-stagger-delay, data-reveal-origin: The entrance timing and where it begins (see staggerReveal.js)
 * - data-reveal-mobile-animation: Entrance below the md breakpoint instead of data-reveal-animation; 'none' skips it there
 * - data-reveal-trigger: 'visible' starts the entrance once half of the carousel is on screen, or once the page rests with any part of it on screen (default); 'manual' leaves it to a page script that calls runReveal() on the instance
 *
 * Inside the root, the module looks up:
 * - [data-focal-carousel-viewport]: The scroller
 * - [data-focal-carousel-strip]: The card strip, stuck to the scrollport
 * - [data-focal-carousel-spacer]: The scrolled spacer behind the strip
 * - [data-focal-carousel-item]: The cards
 * - [data-focal-carousel-prev] / [data-focal-carousel-next]: Optional arrow buttons
 *
 * @param {HTMLElement|null} root - The carousel root element
 * @returns {Promise<import('../modules/focalCarousel.js').FocalCarousel|undefined>} The carousel instance, or undefined when initialization is skipped
 */
export async function focalCarousel(root) {
    if (!root) {
        return;
    }

    if (root.focalCarouselInstance) {
        return root.focalCarouselInstance;
    }

    const initialization = initializations.get(root) ?? {};
    initializations.set(root, initialization);

    modulePromise ??= import('../modules/focalCarousel.js').catch((error) => {
        modulePromise = null;
        throw error;
    });
    const {FocalCarousel} = await modulePromise;
    if (initializations.get(root) !== initialization || !root.isConnected) {
        return;
    }
    if (root.focalCarouselInstance) {
        return root.focalCarouselInstance;
    }

    const instance = new FocalCarousel(root, {
        initialIndex: parseInt(root.dataset.initialIndex ?? '0', 10) || 0,
        autoplay: root.dataset.autoplay === 'true',
        speed: root.dataset.speed === undefined ? undefined : parseFloat(root.dataset.speed),
        interactive: root.dataset.interactive !== 'false',
        scaleUp: root.dataset.scaleUp === 'true',
        reveal: root.dataset.revealAnimation
            ? resolveStaggerOptions({
                animationType: root.dataset.revealAnimation,
                easing: root.dataset.revealEasing,
                duration: root.dataset.revealDuration,
                staggerDelay: root.dataset.revealStaggerDelay,
                origin: root.dataset.revealOrigin,
                mobileAnimationType: root.dataset.revealMobileAnimation,
            })
            : null,
        revealTrigger: root.dataset.revealTrigger === 'manual' ? 'manual' : 'visible',
    });
    instance.init();

    root.focalCarouselInstance = instance;

    return instance;
}

/**
 * Lazily initializes focal carousels shortly before they enter the viewport.
 */
function observeCarousels() {
    const carousels = document.querySelectorAll('[data-focal-carousel]');

    if (carousels.length === 0) {
        return;
    }

    observer ??= new IntersectionObserver((entries) => {
        for (const entry of entries) {
            if (entry.isIntersecting && observed.has(entry.target)) {
                observed.delete(entry.target);
                observer.unobserve(entry.target);
                focalCarousel(/** @type {HTMLElement} */ (entry.target));
            }
        }
    }, {rootMargin: '200px 0px'});

    for (const carousel of carousels) {
        if (!carousel.focalCarouselInstance && !observed.has(carousel)) {
            observed.add(carousel);
            observer.observe(carousel);
        }
    }
}

document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', observeCarousels)
    : observeCarousels();

document.addEventListener('htmx:load', observeCarousels);
document.addEventListener('htmx:beforeCleanupElement', (event) => {
    const carousel = event.target;
    if (carousel instanceof HTMLElement && carousel.matches('[data-focal-carousel]')) {
        observed.delete(carousel);
        observer?.unobserve(carousel);
        initializations.delete(carousel);
        carousel.focalCarouselInstance?.destroy();
    }
});
